import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { useMemo, useState } from 'react'

import { fetchDelegations } from '@/api'
import CopyWithTooltip from '@/common/CopyWithTooltip'
import GradientWrapper from '@/common/GradientWrapper'
import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import ValidatorSelect from '@/common/ValidatorSelect'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  emitter,
  ErrorHandler,
  formatAddress,
  formatBridgeAtomic,
} from '@/helpers'
import {
  buildNativeRedelegateMsg,
  NativeRedelegateError,
} from '@/libs/keplr/messages'
import { queryKeys } from '@/queries/keys'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

interface NativeRedelegateModalProps {
  open: boolean
  onClose: () => void
}

export default function NativeRedelegateModal({
  open,
  onClose,
}: NativeRedelegateModalProps) {
  const { address, signAndBroadcast } = useKeplrWallet()
  const queryClient = useQueryClient()

  // Fetch current delegation
  const { data: delegationData, isLoading: isLoadingDelegation } = useQuery({
    queryKey: queryKeys.staking.delegations(address ?? ''),
    enabled: Boolean(open && address),
    queryFn: async () => fetchDelegations(address!),
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  // Build list of all delegations with their amounts
  const delegations = useMemo(() => {
    return (delegationData?.delegation_responses ?? [])
      .map(delegationResponse => {
        const balanceAmount = delegationResponse?.balance?.amount ?? ''
        const validator =
          delegationResponse?.delegation?.validator_address ?? ''

        if (!balanceAmount || !validator) return null

        return {
          validator,
          balanceAmount,
        }
      })
      .filter(
        (
          d,
        ): d is {
          validator: string
          balanceAmount: string
        } => d !== null,
      )
  }, [delegationData])

  // Selected source and destination validators
  const [validatorSrc, setValidatorSrc] = useState('')
  const [validatorDst, setValidatorDst] = useState('')

  // Find the delegation for selected source validator
  const selectedDelegation = useMemo(() => {
    return delegations.find(d => d.validator === validatorSrc)
  }, [delegations, validatorSrc])

  // Auto-select first validator when modal opens
  useMemo(() => {
    if (open && delegations.length > 0 && !validatorSrc) {
      setValidatorSrc(delegations[0].validator)
    }
  }, [open, delegations, validatorSrc])

  const isSameValidator = Boolean(
    validatorSrc && validatorDst && validatorSrc === validatorDst,
  )

  const redelegateTx = useMutation({
    mutationKey: queryKeys.tx.nativeRedelegate(address ?? ''),
    mutationFn: async () => {
      if (!address) throw new Error('Wallet not connected')

      if (!selectedDelegation || !validatorSrc || !validatorDst) {
        throw new Error('No delegation or validator selected')
      }

      const msg = buildNativeRedelegateMsg({
        delegatorAddress: address,
        validatorSrcAddress: validatorSrc,
        validatorDstAddress: validatorDst,
        amount: {
          denom: bridgelessChainInfo.stakeCurrency.coinMinimalDenom,
          amount: selectedDelegation.balanceAmount,
        },
      })

      return signAndBroadcast([msg])
    },

    onSuccess: txHash => {
      emitter.emit('success', {
        title: 'Redelegation sent',
        message: `TxHash: ${formatAddress(txHash)}`,
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.staking.delegations(address ?? ''),
      })
      onClose()
    },
    onError: error => {
      ErrorHandler.process(NativeRedelegateError.fromUnknown(error))
    },
  })

  const isSubmitting = redelegateTx.isPending

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={v => !v && !isSubmitting && onClose()}
      title={<span className='text-foreground'>Redelegate Native Token</span>}
      content={
        <div className='text-foreground mt-4 flex flex-col gap-4 p-1'>
          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Your Address</span>
            <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
              <span className='muted-foreground text-sm'>
                {formatAddress(address ?? '', 20, 15)}
              </span>
              <div className='ml-auto'>
                <CopyWithTooltip value={address ?? ''} />
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>
              From Validator
            </span>
            <ValidatorSelect
              value={validatorSrc}
              disabled={isLoadingDelegation || delegations.length === 0}
              triggerClassName='w-full'
              contentClassName='w-full'
              onChange={v => setValidatorSrc(v)}
            />
            {delegations.length > 1 && (
              <span className='text-muted-foreground text-xs'>
                You have {delegations.length} delegations. Select which to
                redelegate.
              </span>
            )}
          </div>

          {selectedDelegation ? (
            <>
              <div className='flex flex-col gap-1'>
                <span className='text-muted-foreground text-xs'>Amount</span>
                <GradientWrapper>
                  <div className='bg-input text-foreground flex h-12 w-full items-center rounded-lg px-3 py-2 text-sm font-medium'>
                    {isLoadingDelegation
                      ? '…'
                      : `${formatBridgeAtomic(selectedDelegation.balanceAmount)} BRIDGE`}
                  </div>
                </GradientWrapper>
                <span className='text-muted-foreground text-xs'>
                  (Full delegation will be redelegated)
                </span>
              </div>

              <div className='flex flex-col gap-1'>
                <span className='text-muted-foreground text-xs'>
                  To Validator
                </span>
                <ValidatorSelect
                  value={validatorDst}
                  disabled={isLoadingDelegation || isSubmitting}
                  triggerClassName='w-full'
                  contentClassName='w-full'
                  onChange={v => setValidatorDst(v)}
                />
                {isSameValidator && (
                  <span className='text-xs text-red-400'>
                    Destination validator must be different
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className='flex flex-col gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3'>
              <div className='flex items-center gap-2'>
                <AlertTriangle className='size-4 text-yellow-500' />
                <span className='text-sm font-semibold text-yellow-500'>
                  No Active Delegation
                </span>
              </div>
              <p className='text-muted-foreground text-xs'>
                {validatorSrc
                  ? 'You have no delegation from this validator.'
                  : 'Please select a source validator to view delegation.'}
              </p>
            </div>
          )}
        </div>
      }
      footer={
        <div className='flex w-full gap-2'>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={onClose}
            disabled={isSubmitting}
          >
            Close
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={
              isSubmitting ||
              isLoadingDelegation ||
              !selectedDelegation ||
              !validatorDst ||
              isSameValidator
            }
            onClick={() => redelegateTx.mutate()}
          >
            {isSubmitting ? (
              <span className='flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Redelegating…
              </span>
            ) : (
              'Redelegate'
            )}
          </UiButton>
        </div>
      }
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-md' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}
